<script lang="ts">
	import emblaCarouselSvelte from 'embla-carousel-svelte'
	import type { EmblaCarouselType } from 'embla-carousel'
	import Field from '#components/field.svelte'
	import Radio from '#components/radio.svelte'
	import Checkbox from '#components/checkbox.svelte'
	import Button from '#components/button.svelte'
	import { storable } from 'kitto/svelte'
	import { scrollto } from '#library/scrollto.ts'

	const fields = storable(
		{
			step: 0,
			student: {
				surname: '',
				firstname: '',
				middlename: '',
				nickname: '',
				gender: '',
				dob: '',
				enrolyear: '',
				board: '',
				school: ''
			},
			education: {
				altschool: [],
				current: '',
				hear: '',
				interests: []
			},
			wellbeing: {
				support: '',
				supportdetails: '',
				psychologist: '',
				psychreport: '',
				notes: ''
			},
			address: {
				country: '',
				address1: '',
				address2: '',
				town: '',
				county: '',
				postcode: ''
			},
			contacts: [
				{
					relationship: '',
					gender: '',
					title: '',
					firstname: '',
					lastname: '',
					occupation: '',
					telephone: '',
					sameaddress: [],
					address: {
						country: '',
						address1: '',
						address2: '',
						town: '',
						county: '',
						postcode: ''
					},
					email: '',
					notifications: [],
					pass: '',
					confirmpass: '',
					opendays: [],
					notes: ''
				}
			]
		},
		'enquire'
	)

	let embla_steps: EmblaCarouselType | undefined = $state()
	let embla_fields: EmblaCarouselType | undefined = $state()

	const year = new Date().getFullYear()
	const steps = [
		'About your child',
		'Current education',
		'Wellbeing & support',
		'Child’s address',
		'Contact details'
	]

	function onInit(event: CustomEvent) {
		embla_steps = event.detail
		embla_fields = event.detail

		if (!embla_fields || !embla_steps) return

		embla_steps.scrollTo($fields.step)
		embla_fields.scrollTo($fields.step)

		embla_fields.on('select', () => {
			window.scrollTo({ top: 0 })
			$fields.step = embla_fields?.selectedScrollSnap() ?? 0
		})
	}

	function previous() {
		embla_fields?.scrollPrev()
		embla_steps!.scrollTo(embla_fields!.selectedScrollSnap())
	}

	function next() {
		embla_fields?.scrollNext()
		embla_steps!.scrollTo(embla_fields!.selectedScrollSnap())
	}

	function reset() {
		scrollto(0)
		embla_fields!.scrollTo(0)
		// eslint-disable-next-line svelte/require-store-reactive-access
		if ('reset' in fields) fields.reset()
	}
</script>

<form class="enquire theme-crocus-on-light-gimson">
	<div class="contain">
		<div class="title">Admissions enquiry</div>
		<div class="steps" use:emblaCarouselSvelte={{ options: { watchDrag: true }, plugins: [] }}>
			<div class="inner">
				{#each steps as step, i (step)}
					<div class="step" class:on={$fields.step === i}>
						{#if $fields.step > i}
							<svg class="tick on">
								<use xlink:href="/images/vector.svg#check"></use>
							</svg>
						{:else}
							<span class="tick">{i + 1}.</span>
						{/if}
						{step}
					</div>
				{/each}
			</div>
		</div>
	</div>
	<div
		class="viewport"
		use:emblaCarouselSvelte={{ options: { watchDrag: false }, plugins: [] }}
		onemblaInit={onInit}
	>
		<div class="items">
			<div class="item">
				<div class="contain">
					<div class="set" role="group">
						<legend class="legend">Pupil’s name</legend>
						<div class="fields">
							<Field
								id="student_surname"
								name="student_surname"
								label="Student’s surname"
								placeholder="Enter student’s surname"
								required
								bind:value={$fields.student.surname}
							/>
							<Field
								id="student_firstname"
								name="student_firstname"
								label="Student’s first name"
								placeholder="Enter first name"
								required
								bind:value={$fields.student.firstname}
							/>
							<Field
								id="student_middlename"
								name="student_middlename"
								label="Student’s middle name(s)"
								placeholder="Enter middle name(s)"
								required
								bind:value={$fields.student.middlename}
							/>
							<Field
								id="student_nickname"
								name="student_nickname"
								label="Preferred first name"
								placeholder="Enter preferred first name"
								instruction="This is the name that your child will be referred to by teachers and used on all school reports. Their formal name (if different) will only appear on their final examination certificates."
								required
								bind:value={$fields.student.nickname}
							/>
						</div>
					</div>
					<div class="set" role="group">
						<legend class="legend">Personal details</legend>
						<div class="fields">
							<Radio
								name="student_gender"
								label="Student's gender"
								options={[
									{ id: 'student_male', value: 'male', label: 'Male' },
									{ id: 'student_female', value: 'female', label: 'Female' },
									{ id: 'student_other', value: 'other', label: 'Non - Binary' }
								]}
								instruction="Gender your child identifies as."
								required
								bind:value={$fields.student.gender}
							/>
							<Field
								type="date"
								id="student_dob"
								name="student_dob"
								label="Date of birth"
								placeholder="Select date of birth"
								required
								bind:value={$fields.student.dob}
							/>
						</div>
					</div>
					<div class="set" role="group">
						<legend class="legend">Bedales pathway</legend>
						<div class="fields">
							<Field
								id="student_year"
								name="student_year"
								label="Academic enrolment year"
								placeholder="Select enrolment year"
								instruction="Which academic year are you enquiring about? (e.g. {year + 1}-{year + 2})"
								required
								bind:value={$fields.student.enrolyear}
							/>
							<Field
								id="student_board"
								name="student_board"
								label="Boarding option"
								placeholder="Select boarding option"
								instruction="Please note: Flexi-boarding and half-boarding is available at Bedales Prep only."
								required
								bind:value={$fields.student.board}
							/>
							<Field
								id="student_school"
								name="student_school"
								label="Bedales school you are applying for (Pre-prep, Prep or Senior)"
								placeholder="Select school"
								options={{ pre: 'Pre-prep', prep: 'Prep', senior: 'Senior' }}
								required
								bind:value={$fields.student.school}
							/>
						</div>
					</div>
				</div>
			</div>
			<div class="item">
				<div class="contain">
					<div class="set" role="group">
						<legend class="legend">Your child’s education background</legend>
						<div class="fields">
							<Checkbox
								name="altschool"
								options={[
									{ id: 'preschool', value: 'preschool', label: 'My child is not yet of school age' },
									{ id: 'homeschool', value: 'homeschool', label: 'My child is home educated' }
								]}
								bind:value={$fields.education.altschool}
							/>
							{#if $fields?.education?.altschool?.length === 0}
								<Field
									id="current"
									name="current"
									label="Current school"
									placeholder="Enter current school name and location"
									options={['Pre-prep', 'Prep', 'Senior']}
									type="datalist"
									required
									bind:value={$fields.education.current}
								/>
							{/if}
							<Field
								id="hear"
								name="hear"
								label="Where did you hear about Bedales?"
								placeholder="Select option"
								options={{
									friend: 'Friend',
									website: 'Website',
									other: 'Other'
								}}
								required
								bind:value={$fields.education.hear}
							/>
							<Field
								id="interest"
								name="interest"
								label="Why are you interested in Bedales?"
								placeholder="Select option(s)"
								instruction="Select up to three options"
								options={{
									academic: 'Academic excellence',
									athletics: 'Athletics',
									arts: 'Arts',
									community: 'Community',
									other: 'Other'
								}}
								multiple
								required
								bind:value={$fields.education.interests as never}
							/>
						</div>
					</div>
				</div>
			</div>
			<div class="item">
				<div class="contain">
					<div class="set" role="group">
						<legend class="legend">Applicant support needs</legend>
						<div class="fields">
							<Radio
								name="support"
								label="Does the applicant currently receive any additional learning support or need any exam access arrangements?"
								options={[
									{ id: 'supyes', value: 'yes', label: 'Yes' },
									{ id: 'supno', value: 'no', label: 'No' }
								]}
								instruction="E.g. Extra time, typing, etc."
								bind:value={$fields.wellbeing.support}
							/>
							{#if $fields.wellbeing.support === 'yes'}
								<Field
									type="textarea"
									id="notes"
									name="notes"
									label="Please give details."
									placeholder="E.g. Extra time, enlarged print, a reader or scribe, sensory or movement breaks, etc."
									bind:value={$fields.wellbeing.supportdetails}
								/>
							{/if}
							<Radio
								name="psychologist"
								label="Has the applicant ever been assessed by an Educational Psychologist?"
								options={[
									{ id: 'psyyes', value: 'yes', label: 'Yes' },
									{ id: 'psyno', value: 'no', label: 'No' }
								]}
								bind:value={$fields.wellbeing.psychologist}
							/>
							{#if $fields.wellbeing.psychologist === 'yes'}
								<Field
									type="file"
									id="report"
									name="report"
									label="Please upload a copy of the Educational Psychologist’s report."
								/>
							{/if}
							<Field
								type="textarea"
								id="notes"
								name="notes"
								label="Additional notes"
								placeholder="E.g. Additional support needs, past assessments, or relevant learning information."
								bind:value={$fields.wellbeing.notes}
							/>
						</div>
					</div>
				</div>
			</div>
			<div class="item">
				<div class="contain">
					<div class="set" role="group">
						<legend class="legend">Child’s address</legend>
						<div class="fields">
							<Field
								type="select"
								id="country"
								name="country"
								label="Country"
								options={{
									uk: 'United Kingdom',
									us: 'United States',
									other: 'Other'
								}}
								required
								bind:value={$fields.address.country}
							/>
							<Field
								id="address1"
								name="address1"
								label="Address line 1"
								placeholder="Enter house number and street name"
								required
								bind:value={$fields.address.address1}
							/>
							<Field
								id="address2"
								name="address2"
								label="Address line 2"
								placeholder="Enter flat, suite, unit, or building name"
								bind:value={$fields.address.address2}
							/>
							<Field
								id="town"
								name="town"
								label="Town"
								placeholder="Enter town or city name"
								required
								bind:value={$fields.address.town}
							/>
							<Field
								id="county"
								name="county"
								label="County"
								placeholder="Enter county or region"
								required
								bind:value={$fields.address.county}
							/>
							<Field
								id="postcode"
								name="postcode"
								label="Postcode"
								placeholder="Enter your postcode"
								required
								bind:value={$fields.address.postcode}
							/>
						</div>
					</div>
				</div>
			</div>
			<div class="item">
				<div class="contain">
					<div class="set" role="group">
						<legend class="legend">Parent / Guardian contact details</legend>
						<div class="fields">
							<Field
								id="relationship"
								name="relationship"
								label="Relationship to the applicant"
								placeholder="Select relationship"
								options={{ mother: 'Mother', father: 'Father', guardian: 'Guardian' }}
								required
								bind:value={$fields.contacts[0].relationship}
							/>
							<Radio
								name="gender"
								label="Gender"
								options={[
									{ id: 'male', value: 'male', label: 'Male' },
									{ id: 'female', value: 'female', label: 'Female' },
									{ id: 'other', value: 'other', label: 'Non - Binary' }
								]}
								instruction="Gender you identify as."
								required
								bind:value={$fields.contacts[0].gender}
							/>
							<Field
								id="title"
								name="title"
								label="Title"
								placeholder="Select title"
								options={{
									mr: 'Mr',
									mrs: 'Mrs',
									ms: 'Ms',
									miss: 'Miss',
									dr: 'Dr',
									prof: 'Prof',
									other: 'Other'
								}}
								required
								bind:value={$fields.contacts[0].title}
							/>
							<Field
								id="firstname"
								name="firstname"
								label="First name"
								placeholder="Enter first name"
								required
								bind:value={$fields.contacts[0].firstname}
							/>
							<Field
								id="lastname"
								name="lastname"
								label="Last name"
								placeholder="Enter last name"
								required
								bind:value={$fields.contacts[0].lastname}
							/>
							<Field
								id="occupation"
								name="occupation"
								label="Occupation"
								placeholder="Enter occupation"
								required
								bind:value={$fields.contacts[0].occupation}
							/>
							<Field
								id="telephone"
								name="telephone"
								label="Telephone"
								placeholder="Enter telephone number"
								required
								bind:value={$fields.contacts[0].telephone}
							/>
						</div>
					</div>
					<div class="set" role="group">
						<legend class="legend">Address</legend>
						<div class="fields">
							<Checkbox
								name="sameaddress"
								options={[
									{ id: 'sameaddress', value: 'sameaddress', label: 'Address same as first applicant' }
								]}
								bind:value={$fields.contacts[0].sameaddress}
							/>
						</div>
					</div>
					<div class="set" role="group">
						<legend class="legend">Credentials</legend>
						<div class="fields">
							<Field
								id="email"
								type="email"
								name="email"
								label="Email"
								placeholder="Enter email"
								required
								bind:value={$fields.contacts[0].email}
							/>
							<Checkbox
								name="notifications"
								options={[
									{ id: 'notifications', value: 'notifications', label: 'Receive email notifications' }
								]}
								bind:value={$fields.contacts[0].notifications}
							/>
							<Field
								type="password"
								id="password"
								name="password"
								label="Set password"
								placeholder="Enter password"
								required
								bind:value={$fields.contacts[0].pass}
							/>
							<Field
								type="password"
								id="confirmpassword"
								name="confirmpassword"
								label="Confirm password"
								placeholder="Enter password confirmation"
								required
								bind:value={$fields.contacts[0].confirmpass}
							/>
							<Button onclick={() => {}} outline>Add parent/guardian</Button>
						</div>
					</div>
					<hr />
					<div class="set" role="group">
						<legend class="legend">Parent / Guardian contact details</legend>
						<div class="fields">
							<Checkbox
								name="opendays"
								options={[
									{
										id: 'opendays',
										value: 'opendays',
										label: "I'm interested in Open Days"
									}
								]}
								bind:value={$fields.contacts[0].opendays}
							/>
							<Field
								type="textarea"
								id="additional"
								name="additional"
								label="Any other information regarding your enquiry:"
								placeholder="E.g. Extra time, enlarged print, a reader or scribe, sensory or movement breaks, etc."
								bind:value={$fields.contacts[0].notes}
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
	<div class="buttons contain">
		{#if $fields.step === 0}
			<Button onclick={reset} outline icon="close" label="Reset form"></Button>
		{:else}
			<Button onclick={previous} outline icon="arrow-left" label="Previous Step"></Button>
		{/if}
		{#if $fields.step < steps.length - 1}
			<Button onclick={next} fill>Next</Button>
		{:else}
			<Button type="submit" fill>Submit enquiry</Button>
		{/if}
	</div>
</form>

<style lang="css">
	.enquire {
		padding-block: 1.5rem 8rem;

		&::after {
			background-image: var(--pattern);
			content: '';
			display: block;
			height: 1.5rem;
			position: absolute;
			inset: auto 0 0;
		}
	}

	.title {
		color: var(--c-foreground);
		font: 600 var(--f-h1);
		letter-spacing: var(--ls-h1);
		padding-block-end: 2rem;
	}

	.steps {
		background-image: linear-gradient(to bottom, var(--c-background) 50%, transparent);
		font: 600 var(--f-h5);
		letter-spacing: var(--ls-h5);
		overflow: clip;
		position: sticky;
		top: 4rem;
		z-index: 1;

		.on {
			color: var(--c-foreground);
			text-decoration: underline;
		}
	}

	.inner {
		display: flex;
		gap: 5.625rem;
		padding-block: 1.25rem 2.5rem;
	}

	.step {
		align-items: center;
		display: flex;
		flex: 0 0 auto;
		gap: 0.5rem;

		&:not(:last-child)::after {
			background-color: var(--c-midground);
			content: '';
			inset: 50% auto auto calc(100% + 1.625rem);
			position: absolute;
			size: 1px 2.5rem;
		}
	}

	.tick {
		line-height: 1;
		size: 1.5rem;

		&.on {
			color: var(--c-crocus);
		}
	}

	.set {
		display: grid;
		gap: 2.5rem;
		padding-block: 1.25rem;

		@media (--from-mobile) {
			grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
		}
	}

	.viewport {
		overflow: clip;
	}

	.items {
		display: flex;
	}

	.item {
		flex: 1 0 100%;
	}

	.legend {
		color: var(--c-midground);
		font: 600 var(--f-h5);
		letter-spacing: var(--ls-h5);
	}

	.fields {
		display: flex;
		flex-direction: column;
		gap: 1.625rem;
	}

	.buttons {
		display: grid;
		grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
		gap: 2.5rem;
		margin-block-start: 1.25rem;
		position: sticky;
		bottom: 0;
		background-image: linear-gradient(to top, var(--c-background) 50%, transparent);
		padding-block: 1.25rem;

		:global(.button:only-of-type) {
			grid-column: 2;
		}
	}
</style>
